import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, Table, Button, Spin, Alert, Descriptions, Typography, Space, Divider, Tag } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { PrinterOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import dayjs from 'dayjs';
import { theme } from '../../theme';

const { Title, Text } = Typography;

interface Empresa {
  id: number;
  denominacion: string;
  direccion: string;
  mail: string;
  telefono: string;
  localidad_nombre: string;
  civa_nombre: string;
  tdoc_nombre: string;
  actividad_nombre: string;
}

interface Sucursal {
  id: number;
  denominacion: string;
  direccion: string;
  mail: string;
  telefono: string;
  loc_nombre: string;
  empre_nombre: string;
}

interface Orden {
  id: number;
  nro_orden: number;
  fecha: string;
  producto_nombre: string;
  nproducto_id: number;
  id_contrato: number;
  nro_contrato: string;
  tipo: string;
  tipo_display: string;
  contrato_comprador_nombre: string;
  contrato_comprador_id: number;
  contrato_vendedor_nombre: string;
  contrato_vendedor_id: number;
  destino_nombre: string;
  destino_id: number;
  empresa_nombre: string;
  empresa_id: number;
  sucursal_nombre: string;
  sucursal_id: number;
  sucursal_otra_nombre: string;
  sucursal_otra_id: number;
  motivo: number;
  motivo_display: string;
  descripcion_motivo: string;
  estado: string;
  alta: number;
}

interface OrdenDestino {
  id: number;
  orden_id: number;
  empresa: string;
  direccion: string;
  localidad_nombre: string;
  provincia_nombre: string;
  alta: number;
}

interface OrdenDetalle {
  id: number;
  orden_id: number;
  origen_nombre: string;
  origen_id: number;
  cliente_nombre: string;
  cliente_id: number;
  id_fardo: number;
  nro_fardo: number;
  nro_lote: number;
  empresa_nombre: string;
  empresa_id: number;
  sucursal_nombre: string;
  sucursal_id: number;
  silobolsa: number;
  partesilobolsa: number;
  alta: number;
  kilos_calculados?: number; // Campo calculado para distribuir los kilos
}

interface RecepcionDespacho {
  imputada: boolean;
  nro_recdes?: number;
  tipo?: string;
  fecha?: string;
  kilos_balanza?: number;
  kilos_primera?: number;
  kilos_segunda?: number;
  numero_ticket?: number;
}

interface EmisionData {
  orden: Orden;
  empresa: Empresa;
  sucursal: Sucursal;
  destino: OrdenDestino;
  detalles: OrdenDetalle[];
  total_fardos: number;
  total_kilos: number;
  recepcion_despacho?: RecepcionDespacho;
  kilos_balanza?: number;
}

const styles = {
  container: {
    padding: '20px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: '20px',
  },
  title: {
    color: 'white',
    margin: 0,
    backgroundColor: theme.colors.primaryDark,
    padding: '12px 24px',
    borderRadius: '6px',
    textAlign: 'center' as const,
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    gap: '10px',
    marginTop: '20px',
  },
  backButton: {
    padding: '8px 16px',
    backgroundColor: '#6c757d',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  printButton: {
    padding: '8px 16px',
    backgroundColor: '#28a745',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  card: {
    marginBottom: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  cardHeader: {
    backgroundColor: theme.colors.primaryDark,
    color: 'white',
    textAlign: 'center' as const,
    padding: '12px',
    borderRadius: '6px 6px 0 0',
    margin: '-1px -1px 0 -1px',
  },
  tableContainer: {
    marginTop: '20px',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '200px',
  },
  errorContainer: {
    marginTop: '20px',
  },
  summaryCard: {
    backgroundColor: '#f8f9fa',
    border: '1px solid #dee2e6',
  },
  statusTag: {
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 'bold',
    textAlign: 'center' as const,
  },
  statusActiva: {
    backgroundColor: '#d4edda',
    color: '#155724',
  },
  statusBaja: {
    backgroundColor: '#f8d7da',
    color: '#721c24',
  },
  statusCerrada: {
    backgroundColor: '#d1ecf1',
    color: '#0c5460',
  },
  statusOtros: {
    backgroundColor: '#f8f9fa',
    color: '#6c757d',
  },
  tipoTag: {
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: 'bold',
    textAlign: 'center' as const,
  },
  tipoCompra: {
    backgroundColor: '#d1ecf1',
    color: '#0c5460',
  },
  tipoVenta: {
    backgroundColor: '#d4edda',
    color: '#155724',
  },
  tipoOtros: {
    backgroundColor: '#fff3cd',
    color: '#856404',
  },
};

const EmisionOrdenes: React.FC = () => {
  const { getAccessTokenSilently, user, isAuthenticated } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState<EmisionData | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Obtener datos del estado de navegación
  const { id_orden, nro_orden } = location.state || {};
  console.log('Estado de navegación:', location.state);
  console.log('ID de la orden recibido:', id_orden);
  console.log('Nro de la orden recibido:', nro_orden);

  useEffect(() => {
    const cargarDatos = async () => {
      try {
        if (!isAuthenticated) {
          setError('Por favor, inicie sesión para ver los datos');
          setCargando(false);
          return;
        }
        console.log('ID de la orden:', id_orden);
        if (!id_orden) {
          setError('No se proporcionó el ID de la orden');
          setCargando(false);
          return;
        }

        const id_empresa = user?.id_empresa;
        if (!id_empresa) {
          setError('No se encontró la empresa asociada a su usuario');
          setCargando(false);
          return;
        }

        const token = await getAccessTokenSilently();
        const response = await fetch(`${process.env.REACT_APP_ORDENES_URL}/emision_ordenes/${id_empresa}/${id_orden}/`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Error al cargar los datos de la orden');
        }

        const datos = await response.json();
        
        // Si la orden fue imputada y tiene datos de balanza, calcular kilos por fardo
        if (datos.recepcion_despacho?.imputada && datos.recepcion_despacho.kilos_primera !== undefined && datos.recepcion_despacho.kilos_segunda !== undefined) {
          // Calcular total de kilos como valor absoluto de (kilos_primera - kilos_segunda)
          const totalKilos = Math.abs(datos.recepcion_despacho.kilos_primera - datos.recepcion_despacho.kilos_segunda);
          
          // Actualizar total_kilos
          datos.total_kilos = totalKilos;
          
          // Calcular kilos por fardo
          const cantidadFardos = datos.detalles.length;
          if (cantidadFardos > 0) {
            const kilosPorFardo = totalKilos / cantidadFardos;
            
            // Asignar kilos calculados a cada fardo
            datos.detalles = datos.detalles.map((detalle: OrdenDetalle) => ({
              ...detalle,
              kilos_calculados: kilosPorFardo
            }));
          }
        }
        
        setData(datos);
        setCargando(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error desconocido');
        setCargando(false);
      }
    };

    cargarDatos();
  }, [getAccessTokenSilently, user, isAuthenticated, id_orden]);

  const handleVolver = () => {
    navigate('/listadoordenes');
  };

  const getStatusStyle = (estado: string) => {
    switch (estado) {
      case 'Activa':
        return { ...styles.statusTag, ...styles.statusActiva };
      case 'Baja':
        return { ...styles.statusTag, ...styles.statusBaja };
      case 'Cerrada':
        return { ...styles.statusTag, ...styles.statusCerrada };
      default:
        return { ...styles.statusTag, backgroundColor: '#f8f9fa', color: '#6c757d' };
    }
  };

  const getTipoStyle = (tipo: string) => {
    switch (tipo) {
      case 'C':
        return { ...styles.tipoTag, ...styles.tipoCompra };
      case 'V':
        return { ...styles.tipoTag, ...styles.tipoVenta };
      default:
        return { ...styles.tipoTag, ...styles.tipoOtros };
    }
  };

  const handleGenerarPDF = () => {
    if (!data) return;

    // Crear nuevo documento PDF
    const doc = new jsPDF();
    
    // Configurar fuente y tamaños
    doc.setFont('helvetica');
    doc.setFontSize(16);
    
    // Título principal
    doc.text('EMISIÓN ORDEN', 105, 20, { align: 'center' });
    
    let yPosition = 40;
    
    // DATOS DE LA EMPRESA
    doc.setFontSize(14);
    doc.setTextColor(24, 144, 255); // Azul como en pantalla
    doc.text('DATOS DE LA EMPRESA', 20, yPosition);
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    yPosition += 15;
    
    doc.text(`Empresa: ${data.empresa.denominacion}`, 20, yPosition);
    yPosition += 8;
    doc.text(`Dirección: ${data.empresa.direccion}`, 20, yPosition);
    yPosition += 8;
    doc.text(`Localidad: ${data.empresa.localidad_nombre}`, 20, yPosition);
    yPosition += 8;
    doc.text(`Tipo Responsable: ${data.empresa.civa_nombre}`, 20, yPosition);
    yPosition += 8;
    doc.text(`Teléfono: ${data.empresa.telefono}`, 20, yPosition);
    yPosition += 8;
    doc.text(`Email: ${data.empresa.mail}`, 20, yPosition);
    
    yPosition += 15;
    
    // DATOS DE LA SUCURSAL
    doc.setFontSize(14);
    doc.setTextColor(24, 144, 255);
    doc.text('DATOS DE LA SUCURSAL', 20, yPosition);
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    yPosition += 15;
    
    doc.text(`Sucursal: ${data.sucursal.denominacion}`, 20, yPosition);
    yPosition += 8;
    doc.text(`Dirección: ${data.sucursal.direccion}`, 20, yPosition);
    yPosition += 8;
    doc.text(`Localidad: ${data.sucursal.loc_nombre}`, 20, yPosition);
    yPosition += 8;
    doc.text(`Teléfono: ${data.sucursal.telefono}`, 20, yPosition);
    yPosition += 8;
    doc.text(`Email: ${data.sucursal.mail}`, 20, yPosition);
    
    yPosition += 15;
    
    // DATOS DE LA ORDEN
    doc.setFontSize(14);
    doc.setTextColor(24, 144, 255);
    doc.text('DATOS DE LA ORDEN', 20, yPosition);
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    yPosition += 15;
    
    doc.text(`Nro. Orden: ${data.orden.nro_orden}`, 20, yPosition);
    yPosition += 8;
    doc.text(`Fecha: ${dayjs(data.orden.fecha).format('DD/MM/YYYY')}`, 20, yPosition);
    yPosition += 8;
    doc.text(`Producto: ${data.orden.producto_nombre}`, 20, yPosition);
    yPosition += 8;
    doc.text(`Tipo: ${data.orden.tipo_display}`, 20, yPosition);
    yPosition += 8;
    doc.text(`Contrato: ${data.orden.nro_contrato}`, 20, yPosition);
    yPosition += 8;
    doc.text(`Comprador: ${data.orden.contrato_comprador_nombre || 'No especificado'}`, 20, yPosition);
    yPosition += 8;
    doc.text(`Vendedor: ${data.orden.contrato_vendedor_nombre || 'No especificado'}`, 20, yPosition);
    yPosition += 8;
    doc.text(`Destino: ${data.orden.destino_nombre || 'No especificado'}`, 20, yPosition);
    yPosition += 8;
    doc.text(`Motivo: ${data.orden.motivo_display}`, 20, yPosition);
    yPosition += 8;
    doc.text(`Descripción Motivo: ${data.orden.descripcion_motivo || 'No especificado'}`, 20, yPosition);
    yPosition += 8;
    
    // Estado de la orden (resaltado)
    doc.setFontSize(12);
    doc.setTextColor(220, 53, 69); // Rojo para resaltar
    doc.text(`Estado: ${data.orden.estado}`, 20, yPosition);
    doc.setTextColor(0, 0, 0);
    
    yPosition += 20;
    
    // DATOS DEL DESTINO (si existe)
    if (data.destino) {
      doc.setFontSize(14);
      doc.setTextColor(24, 144, 255);
      doc.text('DATOS DEL DESTINO', 20, yPosition);
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(10);
      yPosition += 15;
      
      doc.text(`Empresa: ${data.destino.empresa}`, 20, yPosition);
      yPosition += 8;
      doc.text(`Dirección: ${data.destino.direccion}`, 20, yPosition);
      yPosition += 8;
      doc.text(`Localidad: ${data.destino.localidad_nombre}`, 20, yPosition);
      yPosition += 8;
      doc.text(`Provincia: ${data.destino.provincia_nombre}`, 20, yPosition);
      yPosition += 20;
    }
    
    // Nueva página para detalles
    doc.addPage();
    doc.setFontSize(14);
    doc.setTextColor(24, 144, 255);
    doc.text('DETALLE DE LA MERCADERÍA', 105, 20, { align: 'center' });
    doc.setTextColor(0, 0, 0);
    
    // Tabla de detalles
    const detallesData = data.detalles.map(detalle => [
      detalle.nro_fardo,
      detalle.nro_lote,
      detalle.origen_nombre,
      detalle.cliente_nombre,
      detalle.silobolsa,
      detalle.partesilobolsa,
      0 // kilos no disponible en orden_detalle
    ]);
    
    autoTable(doc, {
      head: [['Nro Fardo', 'Nro Lote', 'Origen', 'Cliente', 'Silo Bolsa', 'Parte Silo', 'Kilos']],
      body: detallesData,
      startY: 30,
      styles: {
        fontSize: 8,
        cellPadding: 2,
      },
      headStyles: {
        fillColor: [24, 144, 255],
        textColor: 255,
        fontStyle: 'bold',
      },
    });
    
    // Resumen al final
    const finalY = (doc as any).lastAutoTable.finalY + 15;
    doc.setFontSize(12);
    doc.setTextColor(24, 144, 255);
    doc.text('RESUMEN:', 20, finalY);
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(10);
    doc.text(`Total Fardos: ${data.total_fardos}`, 20, finalY + 10);
    doc.text(`Total Kilos: ${data.total_kilos}`, 20, finalY + 18);
    
    // Guardar PDF
    doc.save(`Orden_${data.orden.nro_orden}_${dayjs().format('YYYY-MM-DD')}.pdf`);
  };

  const columns: ColumnsType<OrdenDetalle> = [
    {
      title: 'Nro Fardo',
      dataIndex: 'nro_fardo',
      key: 'nro_fardo',
      width: 100,
    },
    {
      title: 'Nro Lote',
      dataIndex: 'nro_lote',
      key: 'nro_lote',
      width: 100,
    },
    {
      title: 'Origen',
      dataIndex: 'origen_nombre',
      key: 'origen_nombre',
      width: 150,
    },
    {
      title: 'Cliente',
      dataIndex: 'cliente_nombre',
      key: 'cliente_nombre',
      width: 150,
    },
    {
      title: 'Silo Bolsa',
      dataIndex: 'silobolsa',
      key: 'silobolsa',
      width: 100,
    },
    {
      title: 'Parte Silo',
      dataIndex: 'partesilobolsa',
      key: 'partesilobolsa',
      width: 100,
    },
    {
      title: 'Kilos',
      dataIndex: 'kilos_calculados',
      key: 'kilos_calculados',
      width: 80,
      render: (value: number) => {
        if (value !== undefined && value !== null) {
          return value.toFixed(2);
        }
        return 'N/A';
      },
    },
  ];

  if (cargando) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.errorContainer}>
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
          />
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div style={styles.container}>
        <div style={styles.errorContainer}>
          <Alert
            message="Error"
            description="No se encontraron datos para mostrar"
            type="warning"
            showIcon
          />
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <Title level={2} style={styles.title}>
          Emisión Orden
        </Title>
      </div>

      <div style={styles.buttonContainer}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={handleVolver}
          style={styles.backButton}
        >
          Volver
        </Button>
        <Button
          icon={<PrinterOutlined />}
          onClick={handleGenerarPDF}
          style={styles.printButton}
        >
          Imprimir PDF
        </Button>
      </div>

      {/* Datos de la Empresa */}
      <Card
        title={<div style={styles.cardHeader}>DATOS DE LA EMPRESA</div>}
        style={styles.card}
      >
        <Descriptions bordered column={4}>
          <Descriptions.Item label="Empresa" span={2}>
            {data.empresa.denominacion}
          </Descriptions.Item>
          <Descriptions.Item label="Dirección" span={2}>
            {data.empresa.direccion}
          </Descriptions.Item>
          <Descriptions.Item label="Localidad">
            {data.empresa.localidad_nombre}
          </Descriptions.Item>
          <Descriptions.Item label="Tipo Responsable">
            {data.empresa.civa_nombre}
          </Descriptions.Item>
          <Descriptions.Item label="Teléfono">
            {data.empresa.telefono}
          </Descriptions.Item>
          <Descriptions.Item label="Email">
            {data.empresa.mail}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Datos de la Sucursal */}
      <Card
        title={<div style={styles.cardHeader}>DATOS DE LA SUCURSAL</div>}
        style={styles.card}
      >
        <Descriptions bordered column={4}>
          <Descriptions.Item label="Sucursal" span={2}>
            {data.sucursal.denominacion}
          </Descriptions.Item>
          <Descriptions.Item label="Dirección" span={2}>
            {data.sucursal.direccion}
          </Descriptions.Item>
          <Descriptions.Item label="Localidad">
            {data.sucursal.loc_nombre}
          </Descriptions.Item>
          <Descriptions.Item label="Teléfono">
            {data.sucursal.telefono}
          </Descriptions.Item>
          <Descriptions.Item label="Email">
            {data.sucursal.mail}
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Datos de la Orden */}
      <Card
        title={<div style={styles.cardHeader}>DATOS DE LA ORDEN</div>}
        style={styles.card}
      >
        <Descriptions bordered column={4}>
          <Descriptions.Item label="Nro. Orden">
            {data.orden.nro_orden}
          </Descriptions.Item>
          <Descriptions.Item label="Fecha">
            {dayjs(data.orden.fecha).format('DD/MM/YYYY')}
          </Descriptions.Item>
          <Descriptions.Item label="Producto" span={2}>
            {data.orden.producto_nombre}
          </Descriptions.Item>
          <Descriptions.Item label="Tipo">
            <Tag style={getTipoStyle(data.orden.tipo)}>
              {data.orden.tipo_display}
            </Tag>
          </Descriptions.Item>
          {(data.orden.tipo === 'A' || data.orden.tipo === 'F') && (
            <Descriptions.Item label={data.orden.tipo === 'A' ? 'A Sucursal' : 'Desde Sucursal'}>
              {data.orden.sucursal_otra_nombre || 'No especificado'}
            </Descriptions.Item>
          )}
          <Descriptions.Item label="Contrato">
            {data.orden.nro_contrato}
          </Descriptions.Item>
          <Descriptions.Item label="Comprador" span={2}>
            {data.orden.contrato_comprador_nombre || 'No especificado'}
          </Descriptions.Item>
          <Descriptions.Item label="Vendedor" span={2}>
            {data.orden.contrato_vendedor_nombre || 'No especificado'}
          </Descriptions.Item>
          <Descriptions.Item label="Destino" span={2}>
            {data.orden.destino_nombre || 'No especificado'}
          </Descriptions.Item>
          <Descriptions.Item label="Motivo">
            {data.orden.motivo_display}
          </Descriptions.Item>
          <Descriptions.Item label="Descripción Motivo">
            {data.orden.descripcion_motivo || 'No especificado'}
          </Descriptions.Item>
          <Descriptions.Item label="Estado">
            <Tag style={getStatusStyle(data.orden.estado)}>
              {data.orden.estado}
            </Tag>
          </Descriptions.Item>
        </Descriptions>
      </Card>

      {/* Datos del Destino */}
      {data.destino ? (
        <Card
          title={<div style={styles.cardHeader}>DATOS DEL DESTINO</div>}
          style={styles.card}
        >
          <Descriptions bordered column={4}>
            <Descriptions.Item label="Empresa" span={2}>
              {data.destino.empresa}
            </Descriptions.Item>
            <Descriptions.Item label="Dirección" span={2}>
              {data.destino.direccion}
            </Descriptions.Item>
            <Descriptions.Item label="Localidad">
              {data.destino.localidad_nombre}
            </Descriptions.Item>
            <Descriptions.Item label="Provincia">
              {data.destino.provincia_nombre}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      ) : null}

      {/* Detalle de la Mercadería */}
      <Card
        title={<div style={styles.cardHeader}>DETALLE DE LA MERCADERÍA</div>}
        style={styles.card}
      >
        <div style={styles.tableContainer}>
          <Table
            columns={columns}
            dataSource={data.detalles}
            rowKey="id"
            pagination={false}
            scroll={{ x: 800 }}
            size="small"
          />
        </div>
        
        <Divider />
        
        <div style={styles.summaryCard}>
          <Descriptions column={2}>
            <Descriptions.Item label="Total Fardos">
              <Text strong>{data.total_fardos}</Text>
            </Descriptions.Item>
            <Descriptions.Item label="Total Kilos">
              <Text strong>{data.total_kilos}</Text>
            </Descriptions.Item>
          </Descriptions>
        </div>
      </Card>
    </div>
  );
};

export default EmisionOrdenes;
